import React, { useState } from 'react';
import { View, StyleSheet, ScrollView, Alert } from 'react-native';
import { NativeStackScreenProps } from '@react-navigation/native-stack';
import { RootStackParamList } from '../../navigation/RootNavigator';
import {
  PageContainer,
  ContentContainer,
  HeaderContainer,
  Text,
  Button,
  Card,
  Theme,
} from '../../components/ui';
import { auth, firestore } from '../../config/firebase';
import { signInWithEmailAndPassword, createUserWithEmailAndPassword } from 'firebase/auth';
import { doc, deleteDoc } from 'firebase/firestore';
import { PrivateProfileService } from '../../services/privateProfileService';

type Props = NativeStackScreenProps<RootStackParamList, 'Debug'>;

interface TestScenario {
  id: string;
  name: string;
  description: string;
  email: string;
  password: string;
  profileData: any;
}

/**
 * TransferDebugScreen
 * 
 * Debug screen for testing external service transfer scenarios.
 * Creates test users with different data profiles to test:
 * - AI extraction from raw Q&A
 * - Pre-structured data handling
 * - Different Q&A formats
 * - Edge cases
 */
const TransferDebugScreen: React.FC<Props> = ({ navigation }) => {
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState('');

  const scenarios: TestScenario[] = [
    {
      id: 'raw-qa-only',
      name: '1. Raw Q&A Only (AI Extraction)',
      description: 'User with raw Q&A about leadership.',
      email: 'test-raw-qa@mentorist.com',
      password: 'test123456',
      profileData: {
        onboardingAnswers: {
          sessionId: 'external-raw-qa-test',
          completedAt: Date.now() / 1000,
          version: 'external-v1',
          answers: [
            {
              questionText: 'What are your main goals for personal development?',
              answer: 'I want to become a better leader and improve my work-life balance. Also interested in learning how to manage my team more effectively.',
            },
            {
              questionText: 'What challenges are you currently facing?',
              answer: 'Time management is my biggest issue. I struggle with delegation and often work late hours.',
            },
            {
              questionText: 'What topics interest you most?',
              answer: 'Leadership, psychology, productivity, and personal development. I love reading about emotional intelligence.',
            },
            {
              questionText: 'How would you describe yourself?',
              answer: 'I\'m ambitious and analytical. I tend to overthink things but I\'m also very goal-oriented.',
            },
            {
              questionText: 'Rate your current life satisfaction (1-10)',
              answer: '6',
            },
          ],
        },
        coreProfile: {
          goals: [],
          interests: [],
          challenges: [],
          personalityTraits: [],
          lastUpdated: Date.now() / 1000,
          source: 'external_import',
        },
        activeContext: {
          lastUpdated: Date.now() / 1000,
          source: 'external_import',
          currentFocus: [],
          information: '',
        },
      },
    },
    {
      id: 'parenting-mentor',
      name: '2. Parenting Onboarding (Parenting Mentor)',
      description: 'User from parenting onboarding. Pre-selects parenting mentor.',
      email: 'test-parenting@mentorist.com',
      password: 'test123456',
      profileData: {
        onboardingAnswers: {
          sessionId: 'external-parenting-test',
          completedAt: Date.now() / 1000,
          version: 'external-v1',
          answers: [
            {
              questionText: 'What are your main parenting challenges?',
              answer: 'Getting my 5 and 8 year old kids to listen without having to yell. Bedtime is especially difficult.',
            },
            {
              questionText: 'What parenting style resonates with you?',
              answer: 'I believe in positive parenting and gentle discipline, but I struggle to stay consistent when I\'m tired.',
            },
            {
              questionText: 'What would you like to improve?',
              answer: 'My patience, creating better routines, and not feeling guilty when I need time for myself.',
            },
          ],
        },
        coreProfile: {
          goals: [],
          interests: [],
          challenges: [],
          personalityTraits: [],
          lastUpdated: Date.now() / 1000,
          source: 'external_import',
        },
        activeContext: {
          lastUpdated: Date.now() / 1000,
          source: 'external_import',
          currentFocus: [],
          information: '',
        },
      },
    },
    {
      id: 'pre-structured',
      name: '3. Pre-Structured Data (Leadership + Career)',
      description: 'User with both Q&A and pre-extracted data. Career growth focus.',
      email: 'test-structured@mentorist.com',
      password: 'test123456',
      profileData: {
        onboardingAnswers: {
          sessionId: 'external-structured-test',
          completedAt: Date.now() / 1000,
          version: 'external-v1',
          answers: [
            { questionText: 'Your goals?', answer: 'Career growth and health' },
            { questionText: 'Challenges?', answer: 'Stress and time' },
          ],
        },
        coreProfile: {
          goals: ['Career advancement', 'Better health', 'Financial freedom'],
          interests: ['Business', 'Wellness', 'Investing'],
          challenges: ['Stress management', 'Time constraints'],
          personalityTraits: ['Driven', 'Perfectionist'],
          lastUpdated: Date.now() / 1000,
          source: 'external_import',
        },
        activeContext: {
          lastUpdated: Date.now() / 1000,
          source: 'external_import',
          currentFocus: ['Career advancement'],
          information: 'User is focused on career growth while maintaining health.',
        },
      },
    },
    {
      id: 'json-answers',
      name: '4. JSON Format Answers (Leadership Coach)',
      description: 'User with JSON-formatted answers. Leadership & productivity focus.',
      email: 'test-json@mentorist.com',
      password: 'test123456',
      profileData: {
        onboardingAnswers: {
          sessionId: 'external-json-test',
          completedAt: Date.now() / 1000,
          version: 'external-v1',
          answers: [
            {
              questionText: 'Select your focus areas',
              answer: JSON.stringify({
                areas: ['leadership', 'health', 'relationships'],
                priority: 'leadership',
                urgency: 'high',
              }),
            },
            {
              questionText: 'Rate your satisfaction in different areas',
              answer: JSON.stringify({
                career: 7,
                health: 5,
                relationships: 8,
                personal_growth: 6,
              }),
            },
            {
              questionText: 'What would you like to improve?',
              answer: 'I need to work on delegation, stress management, and creating better boundaries between work and personal life.',
            },
          ],
        },
        coreProfile: {
          goals: [],
          interests: [],
          challenges: [],
          personalityTraits: [],
          lastUpdated: Date.now() / 1000,
          source: 'external_import',
        },
        activeContext: {
          lastUpdated: Date.now() / 1000,
          source: 'external_import',
          currentFocus: [],
          information: '',
        },
      },
    },
    {
      id: 'minimal-qa',
      name: '5. Minimal Q&A (Default Mentor)',
      description: 'User with very few questions. No mentor specified (uses default).',
      email: 'test-minimal@mentorist.com',
      password: 'test123456',
      profileData: {
        onboardingAnswers: {
          sessionId: 'external-minimal-test',
          completedAt: Date.now() / 1000,
          version: 'external-v1',
          answers: [
            { questionText: 'What brings you here?', answer: 'Personal growth' },
          ],
        },
        coreProfile: {
          goals: [],
          interests: [],
          challenges: [],
          personalityTraits: [],
          lastUpdated: Date.now() / 1000,
          source: 'external_import',
        },
        activeContext: {
          lastUpdated: Date.now() / 1000,
          source: 'external_import',
          currentFocus: [],
          information: '',
        },
      },
    },
    {
      id: 'long-form',
      name: '6. Long-Form Responses (Leadership Coach)',
      description: 'User with very detailed long answers about personal development.',
      email: 'test-longform@mentorist.com',
      password: 'test123456',
      profileData: {
        onboardingAnswers: {
          sessionId: 'external-longform-test',
          completedAt: Date.now() / 1000,
          version: 'external-v1',
          answers: [
            {
              questionText: 'Tell us about yourself and your journey',
              answer: 'I\'m a 35-year-old product manager at a tech startup. Over the past 10 years, I\'ve climbed the corporate ladder but recently realized I\'ve been sacrificing my health and relationships in the process. I started reading self-help books about 2 years ago and discovered a passion for personal development. I meditate daily, journal regularly, and I\'m always looking for ways to improve. My main challenges right now are delegation at work, managing stress, and finding time for exercise. I tend to be a perfectionist which both helps and hurts me. I\'m analytical by nature and love data-driven approaches to self-improvement.',
            },
            {
              questionText: 'What specific changes do you want to make in your life?',
              answer: 'First, I want to become a better leader by learning to trust my team more and delegate effectively. Second, I need to establish better work-life boundaries - I\'m tired of checking emails at 11pm. Third, I want to develop a consistent exercise routine because I know it helps with stress. Fourth, I\'m interested in improving my emotional intelligence and communication skills. And finally, I want to find more time for hobbies and relationships that bring me joy.',
            },
          ],
        },
        coreProfile: {
          goals: [],
          interests: [],
          challenges: [],
          personalityTraits: [],
          lastUpdated: Date.now() / 1000,
          source: 'external_import',
        },
        activeContext: {
          lastUpdated: Date.now() / 1000,
          source: 'external_import',
          currentFocus: [],
          information: '',
        },
      },
    },
  ];

  const createTestUser = async (scenario: TestScenario) => {
    setLoading(true);
    setStatus(`Creating test user: ${scenario.name}...`);

    try {
      // Step 1: Sign out current user
      if (auth.currentUser) {
        await auth.signOut();
      }

      // Step 2: Try to create or sign in to test account
      let userCredential;
      try {
        userCredential = await createUserWithEmailAndPassword(
          auth,
          scenario.email,
          scenario.password
        );
        setStatus('✅ Created new test user');
      } catch (error: any) {
        if (error.code === 'auth/email-already-in-use') {
          // User exists, sign in
          userCredential = await signInWithEmailAndPassword(
            auth,
            scenario.email,
            scenario.password
          );
          setStatus('✅ Signed in to existing test user');
        } else {
          throw error;
        }
      }

      const userId = userCredential.user.uid;

      // Step 3: Create minimal profile first (this uses proper service layer)
      setStatus('Creating minimal profile...');
      await PrivateProfileService.ensureProfileExists(userId, {
        email: scenario.email,
        displayName: scenario.email.split('@')[0],
      });
      
      // Step 4: Update profile with test scenario data
      setStatus('Adding test data...');
      
      // Update onboarding answers if provided
      if (scenario.profileData.onboardingAnswers) {
        await PrivateProfileService.updateOnboardingData(userId, {
          onboardingAnswers: scenario.profileData.onboardingAnswers,
        });
      }
      
      // Update core profile if provided
      if (scenario.profileData.coreProfile) {
        await PrivateProfileService.updateOnboardingData(userId, {
          coreProfile: scenario.profileData.coreProfile,
        });
      }
      
      // Update active context if provided
      if (scenario.profileData.activeContext) {
        await PrivateProfileService.updateOnboardingData(userId, {
          activeContext: scenario.profileData.activeContext,
        });
      }
      
      setStatus('✅ Test data written successfully!');

      // Step 5: Get the profile to pass to navigation
      const profileResult = await PrivateProfileService.getProfile(userId);
      if (!profileResult.success || !profileResult.data) {
        throw new Error('Failed to load created profile');
      }

      // Step 6: Navigate to abbreviated onboarding to test the flow
      setTimeout(() => {
        setStatus('Navigating to transfer flow...');
        navigation.navigate('AbbreviatedOnboarding', { profile: profileResult.data });
      }, 1000);

    } catch (error: any) {
      console.error('Error creating test user:', error);
      setStatus(`❌ Error: ${error.message}`);
      Alert.alert('Error', error.message);
    } finally {
      setLoading(false);
    }
  };

  const cleanupTestUsers = async () => {
    Alert.alert(
      'Clean Up Test Users',
      'This will delete all test user accounts and their data. Continue?',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Delete All',
          style: 'destructive',
          onPress: async () => {
            setLoading(true);
            setStatus('Cleaning up test users...');

            try {
              // Sign out current user
              if (auth.currentUser) {
                await auth.signOut();
              }

              // Delete Firestore documents
              for (const scenario of scenarios) {
                try {
                  // Sign in to get UID
                  const cred = await signInWithEmailAndPassword(
                    auth,
                    scenario.email,
                    scenario.password
                  );
                  
                  const userId = cred.user.uid;
                  
                  // Delete profile
                  await deleteDoc(doc(firestore, 'privateProfile', userId));
                  
                  // Note: Can't delete auth user from client side
                  // Need to use Firebase Console or Admin SDK
                  
                  setStatus(`✅ Deleted profile for ${scenario.email}`);
                } catch (error) {
                  console.log(`Skipping ${scenario.email} (doesn't exist)`);
                }
              }

              await auth.signOut();
              setStatus('✅ Cleanup complete! (Note: Auth users need admin deletion)');
              
              Alert.alert(
                'Cleanup Complete',
                'Test profiles deleted. Auth users still exist but can be reused.',
              );
            } catch (error: any) {
              setStatus(`❌ Error: ${error.message}`);
              Alert.alert('Error', error.message);
            } finally {
              setLoading(false);
            }
          },
        },
      ]
    );
  };

  return (
    <PageContainer>
      <ScrollView contentContainerStyle={styles.scrollContent}>
        <ContentContainer>
          <HeaderContainer>
            <Text variant="heading2">Transfer Debug</Text>
            <Text variant="body2" color="secondary">
              Test external service transfer scenarios
            </Text>
          </HeaderContainer>

          {status && (
            <Card style={styles.statusCard}>
              <Text variant="body2">{status}</Text>
            </Card>
          )}

          <View style={styles.section}>
            <Text variant="heading3" style={styles.sectionTitle}>
              Test Scenarios
            </Text>
            <Text variant="body2" color="secondary" style={styles.sectionDesc}>
              Each scenario creates a test user and simulates data from external service.
              Click to test the transfer flow!
            </Text>
          </View>

          {scenarios.map((scenario) => (
            <Card key={scenario.id} style={styles.scenarioCard}>
              <Text variant="body1" style={styles.scenarioName}>
                {scenario.name}
              </Text>
              <Text variant="body2" color="secondary" style={styles.scenarioDesc}>
                {scenario.description}
              </Text>
              <Text variant="caption" color="secondary" style={styles.scenarioEmail}>
                {scenario.email}
              </Text>
              <Text variant="caption" color="secondary" style={styles.scenarioQA}>
                {scenario.profileData.onboardingAnswers.answers.length} questions
              </Text>
              <Button
                title={loading ? 'Processing...' : 'Test This Scenario'}
                onPress={() => createTestUser(scenario)}
                disabled={loading}
                style={styles.scenarioButton}
              />
            </Card>
          ))}

          <View style={styles.section}>
            <Text variant="heading3" style={styles.sectionTitle}>
              Utilities
            </Text>
          </View>

          <Button
            title="Clean Up Test Users"
            variant="secondary"
            onPress={cleanupTestUsers}
            disabled={loading}
          />

          <Button
            title="Back to Debug Menu"
            variant="link"
            onPress={() => navigation.goBack()}
            style={styles.backButton}
          />
        </ContentContainer>
      </ScrollView>
    </PageContainer>
  );
};

const styles = StyleSheet.create({
  scrollContent: {
    flexGrow: 1,
    paddingBottom: Theme.spacing.xxl,
  },
  statusCard: {
    padding: Theme.spacing.lg,
    marginBottom: Theme.spacing.lg,
    backgroundColor: Theme.colors.primaryLight,
  },
  section: {
    marginTop: Theme.spacing.xxl,
    marginBottom: Theme.spacing.lg,
  },
  sectionTitle: {
    marginBottom: Theme.spacing.sm,
  },
  sectionDesc: {
    marginBottom: Theme.spacing.md,
  },
  scenarioCard: {
    padding: Theme.spacing.lg,
    marginBottom: Theme.spacing.md,
  },
  scenarioName: {
    fontFamily: Theme.typography.fontFamily.semiBold,
    marginBottom: Theme.spacing.xs,
  },
  scenarioDesc: {
    marginBottom: Theme.spacing.sm,
  },
  scenarioEmail: {
    marginTop: Theme.spacing.xs,
    fontFamily: Theme.typography.fontFamily.regular,
  },
  scenarioQA: {
    marginTop: Theme.spacing.xs,
    fontStyle: 'italic',
  },
  scenarioButton: {
    marginTop: Theme.spacing.md,
  },
  backButton: {
    marginTop: Theme.spacing.xl,
  },
});

export default TransferDebugScreen;

